import React, { useState } from 'react';
import { Download, Upload, FileJson, FileText, File } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = async () => {
  if (typeof chrome !== 'undefined' && chrome.storage) {
    const { accessToken } = await chrome.storage.local.get('accessToken');
    if (accessToken && accessToken !== 'session_active') {
      return { Authorization: `Bearer ${accessToken}` };
    }
  }
  return {};
};

export default function ImportExportSection() {
  const [importing, setImporting] = useState(false);
  const [importFormat, setImportFormat] = useState('json');

  const handleExport = async (format) => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(`${API_URL}/api/prompts/export/${format}`, {
        withCredentials: true,
        headers,
        responseType: format === 'json' ? 'json' : 'blob'
      });

      if (format === 'json') {
        const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `prompts.json`;
        a.click();
      } else {
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `prompts.${format === 'csv' ? 'csv' : 'md'}`;
        a.click();
      }

      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    try {
      const text = await file.text();
      const headers = await getAuthHeaders();
      
      const { data } = await axios.post(
        `${API_URL}/api/prompts/import`,
        {
          format: importFormat,
          data: text
        },
        { withCredentials: true, headers }
      );

      toast.success(data.message);
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      toast.error('Import failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  return (
    <div className="bg-white rounded-xl border border-[#E7E5E4] shadow-sm p-4" data-testid="import-export-section">
      <h3 className="text-xs tracking-wider uppercase text-[#78716C] mb-3">Import / Export</h3>
      
      {/* Export */}
      <div className="mb-4">
        <p className="text-xs text-[#57534E] mb-2">Export your prompts:</p>
        <div className="flex gap-2">
          <button
            onClick={() => handleExport('json')}
            className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-xs font-medium transition-colors border border-[#E7E5E4] bg-white text-[#1C1917] hover:bg-[#FAFAF9] flex-1"
            data-testid="export-json-button"
          >
            <FileJson size={14} className="mr-1" /> JSON
          </button>
          <button
            onClick={() => handleExport('csv')}
            className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-xs font-medium transition-colors border border-[#E7E5E4] bg-white text-[#1C1917] hover:bg-[#FAFAF9] flex-1"
            data-testid="export-csv-button"
          >
            <FileText size={14} className="mr-1" /> CSV
          </button>
          <button
            onClick={() => handleExport('markdown')}
            className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-xs font-medium transition-colors border border-[#E7E5E4] bg-white text-[#1C1917] hover:bg-[#FAFAF9] flex-1"
            data-testid="export-md-button"
          >
            <File size={14} className="mr-1" /> MD
          </button>
        </div>
      </div>

      {/* Import */}
      <div>
        <p className="text-xs text-[#57534E] mb-2">Import prompts:</p>
        <div className="space-y-2">
          <select
            value={importFormat}
            onChange={(e) => setImportFormat(e.target.value)}
            className="rounded-lg border border-[#E7E5E4] bg-white px-3 py-2 text-xs text-[#1C1917] focus:border-[#2C4C3B] focus:ring-1 focus:ring-[#2C4C3B] focus:outline-none w-full"
            data-testid="import-format-select"
          >
            <option value="json">JSON Format</option>
            <option value="csv">CSV Format</option>
          </select>
          
          <label className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-xs font-medium transition-colors border border-[#E7E5E4] bg-white text-[#1C1917] hover:bg-[#FAFAF9] w-full cursor-pointer">
            <Upload size={14} className="mr-1" />
            {importing ? 'Importing...' : 'Choose File'}
            <input
              type="file"
              accept={importFormat === 'json' ? '.json' : '.csv'}
              onChange={handleImport}
              className="hidden"
              disabled={importing}
              data-testid="import-file-input"
            />
          </label>
        </div>
      </div>
    </div>
  );
}